import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { STATUS_META, SCHEDULE_TIMES } from "@/lib/status";
import { ChevronLeft, ChevronRight, CalendarDays, CalendarRange, User, Clock, CalendarClock } from "lucide-react";

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYmd = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };

const PERIOD_LABEL = { manha: "Manhã", tarde: "Tarde", especial: "Especial" };
// Deriva o período de um chamado: pelo horário agendado, ou pela disponibilidade informada
const periodOf = (r) => {
  if (r.scheduled_time) return r.scheduled_time < "13:00" ? "manha" : "tarde";
  return r.availability_period || null;
};

function EventChip({ r, draggable, onDragStart }) {
  const meta = STATUS_META[r.status] || { label: r.status, color: "bg-slate-100 text-slate-700" };
  const lead = r.scheduled_time || PERIOD_LABEL[r._period] || "—";
  return (
    <div className={`rounded px-1.5 py-1 text-[11px] leading-tight border ${meta.color} truncate ${draggable ? "cursor-move" : ""}`}
      draggable={draggable} onDragStart={onDragStart}
      title={`${r.scheduled_time ? r.scheduled_time + " · " : (PERIOD_LABEL[r._period] ? PERIOD_LABEL[r._period] + " · " : "")}${r.service_type} — ${r.customer_name || ""}`}
      data-testid={`agenda-event-${r.id}`}>
      <span className="font-semibold">{lead}</span>{" "}
      {r.customer_name || r.service_type}
    </div>
  );
}

export default function AgendaView({ requests, technicians = [], showTechFilter = false, onReschedule = null }) {
  const [mode, setMode] = useState("month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [techId, setTechId] = useState("all");
  const [selectedDay, setSelectedDay] = useState(null);

  const scheduled = useMemo(() => {
    return (requests || [])
      .map((r) => ({ ...r, _date: r.scheduled_date || r.availability_date || null, _period: periodOf(r) }))
      .filter((r) => r._date)
      .filter((r) => techId === "all" || r.assigned_technician_id === techId);
  }, [requests, techId]);

  const byDate = useMemo(() => {
    const m = {};
    for (const r of scheduled) {
      (m[r._date] = m[r._date] || []).push(r);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (a.scheduled_time || "99").localeCompare(b.scheduled_time || "99"));
    }
    return m;
  }, [scheduled]);

  const shift = (delta) => {
    const d = new Date(cursor);
    if (mode === "month") d.setMonth(d.getMonth() + delta);
    else d.setDate(d.getDate() + delta * 7);
    setCursor(d);
    setSelectedDay(null);
  };
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setCursor(d); setSelectedDay(null); };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const monthCells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = startOfWeek(first);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const headerLabel = mode === "month"
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : `${weekDays[0].getDate()} ${MONTHS[weekDays[0].getMonth()].slice(0, 3)} – ${weekDays[6].getDate()} ${MONTHS[weekDays[6].getMonth()].slice(0, 3)} ${weekDays[6].getFullYear()}`;

  const selectedList = selectedDay ? (byDate[ymd(selectedDay)] || []) : [];

  return (
    <div className="space-y-4" data-testid="agenda-view">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(-1)} data-testid="agenda-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={goToday} data-testid="agenda-today">Hoje</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(1)} data-testid="agenda-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-2 font-semibold text-slate-800 capitalize" data-testid="agenda-header-label">{headerLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          {showTechFilter && (
            <Select value={techId} onValueChange={(v) => setTechId(v)}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="agenda-tech-filter">
                <SelectValue placeholder="Todos os técnicos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="agenda-tech-all">Todos os técnicos</SelectItem>
                {technicians.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`agenda-tech-${t.id}`}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <Button variant={mode === "month" ? "default" : "ghost"} size="sm"
              className={`h-9 rounded-none gap-1.5 ${mode === "month" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
              onClick={() => setMode("month")} data-testid="agenda-view-mode-month">
              <CalendarDays className="h-4 w-4" /> Mês
            </Button>
            <Button variant={mode === "week" ? "default" : "ghost"} size="sm"
              className={`h-9 rounded-none gap-1.5 ${mode === "week" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
              onClick={() => setMode("week")} data-testid="agenda-view-mode-week">
              <CalendarRange className="h-4 w-4" /> Semana
            </Button>
          </div>
        </div>
      </div>

      {mode === "month" ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="border-slate-200 lg:col-span-2">
            <CardContent className="p-3">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="text-center text-xs font-semibold text-slate-400 py-1">{w}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {monthCells.map((d) => {
                  const key = ymd(d);
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const evs = byDate[key] || [];
                  const isToday = sameDay(d, today);
                  const isSel = selectedDay && sameDay(d, selectedDay);
                  return (
                    <button key={key} onClick={() => setSelectedDay(d)} data-testid={`agenda-day-${key}`}
                      className={`min-h-[74px] rounded-md border p-1 text-left align-top transition-colors
                        ${inMonth ? "bg-white" : "bg-slate-50 text-slate-400"}
                        ${isSel ? "border-blue-500 ring-1 ring-blue-500" : "border-slate-200 hover:border-blue-300"}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold ${isToday ? "bg-blue-600 text-white rounded-full h-5 w-5 flex items-center justify-center" : ""}`}>
                          {d.getDate()}
                        </span>
                        {evs.length > 0 && <span className="text-[10px] text-blue-600 font-semibold">{evs.length}</span>}
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {evs.slice(0, 2).map((r) => <EventChip key={r.id} r={r} />)}
                        {evs.length > 2 && <div className="text-[10px] text-slate-500 pl-1">+{evs.length - 2} mais</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
                <CalendarClock className="h-4 w-4 text-[#1e7be0]" />
                {selectedDay ? selectedDay.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" }) : "Selecione um dia"}
              </h3>
              {selectedDay && selectedList.length === 0 && (
                <p className="text-sm text-slate-400" data-testid="agenda-day-empty">Nenhum atendimento agendado.</p>
              )}
              <div className="space-y-2" data-testid="agenda-day-list">
                {selectedList.map((r) => <DayItem key={r.id} r={r} />)}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="border-slate-200 overflow-x-auto">
          <CardContent className="p-0">
            <div className="min-w-[720px]">
              <div className="grid grid-cols-8 border-b border-slate-200 bg-slate-50 sticky top-0">
                <div className="p-2 text-xs font-semibold text-slate-400">Horário</div>
                {weekDays.map((d) => {
                  const isToday = sameDay(d, today);
                  return (
                    <div key={ymd(d)} className={`p-2 text-center text-xs font-semibold ${isToday ? "text-blue-600" : "text-slate-500"}`}>
                      {WEEKDAYS[d.getDay()]}<br /><span className="text-sm">{d.getDate()}</span>
                    </div>
                  );
                })}
              </div>
              {SCHEDULE_TIMES.map((t) => (
                <div key={t} className="grid grid-cols-8 border-b border-slate-100">
                  <div className="p-1.5 text-[11px] text-slate-400 border-r border-slate-100">{t}</div>
                  {weekDays.map((d) => {
                    const evs = (byDate[ymd(d)] || []).filter((r) => r.scheduled_time === t);
                    return (
                      <div key={ymd(d) + t} className="p-1 min-h-[34px] border-r border-slate-50 space-y-0.5"
                        onDragOver={onReschedule ? (e) => e.preventDefault() : undefined}
                        onDrop={onReschedule ? (e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id) onReschedule(id, ymd(d), t); } : undefined}
                        data-testid={`agenda-slot-${ymd(d)}-${t}`}>
                        {evs.map((r) => (
                          <EventChip key={r.id} r={r} draggable={!!onReschedule}
                            onDragStart={(e) => e.dataTransfer.setData("text/plain", r.id)} />
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
              {/* Sem horário exato — encaixado por período de disponibilidade */}
              {[
                { key: "manha", label: "Manhã (09h–12h)", cls: "bg-amber-50/40 text-amber-700", tid: "agenda-band-manha" },
                { key: "tarde", label: "Tarde (13h–17h)", cls: "bg-orange-50/40 text-orange-700", tid: "agenda-band-tarde" },
                { key: "especial", label: "Horário especial", cls: "bg-purple-50/40 text-purple-700", tid: "agenda-band-especial" },
                { key: null, label: "A combinar", cls: "bg-slate-50 text-slate-500", tid: "agenda-band-combinar" },
              ].map((band) => (
                <div key={band.label} className={`grid grid-cols-8 border-b border-slate-100 ${band.cls}`} data-testid={band.tid}>
                  <div className="p-1.5 text-[11px] border-r border-slate-100">{band.label}</div>
                  {weekDays.map((d) => {
                    const evs = (byDate[ymd(d)] || []).filter((r) => !r.scheduled_time && (r._period || null) === band.key);
                    return (
                      <div key={ymd(d) + "b" + band.label} className="p-1 min-h-[34px] border-r border-slate-50 space-y-0.5">
                        {evs.map((r) => (
                          <EventChip key={r.id} r={r} draggable={!!onReschedule}
                            onDragStart={(e) => e.dataTransfer.setData("text/plain", r.id)} />
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {scheduled.length === 0 && (
        <p className="text-slate-500 py-6 text-center text-sm" data-testid="agenda-empty">
          Nenhum chamado agendado{techId !== "all" ? " para este técnico" : ""}. Defina data/horário ao abrir ou editar o chamado.
        </p>
      )}
    </div>
  );
}

function DayItem({ r }) {
  const meta = STATUS_META[r.status] || { label: r.status, color: "bg-slate-100 text-slate-700" };
  return (
    <div className="rounded-lg border border-slate-200 p-2.5" data-testid={`agenda-day-item-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800 flex items-center gap-1">
          <Clock className="h-3.5 w-3.5 text-slate-400" />{r.scheduled_time || PERIOD_LABEL[r._period] || "A combinar"}
        </span>
        <Badge className={`${meta.color} border text-[11px]`}>{meta.label}</Badge>
      </div>
      <p className="text-sm text-slate-700 mt-1">{r.service_type}{r.equipment ? ` · ${r.equipment}` : ""}</p>
      {r.customer_name && (
        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><User className="h-3 w-3" />{r.customer_name}</p>
      )}
      {r.assigned_technician_name && (
        <p className="text-xs text-slate-500 mt-0.5">Técnico: {r.assigned_technician_name}</p>
      )}
    </div>
  );
}
